import { Injectable, computed, signal } from '@angular/core';

export type BuilderSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

/** Exactly one of ok/fail is honoured; later calls are ignored. */
export interface SaveTicket {
  ok(): void;
  fail(reason?: string): void;
}

/**
 * How long a confirmed save says "Saved HH:MM" when nothing on screen tracks later edits. A pane that
 * reports its own dirty state keeps the tick indefinitely, because it will say when that stops being
 * true; a pane that does not gets a receipt that expires rather than a standing claim.
 */
const UNTRACKED_SAVED_TTL_MS = 12_000;

/**
 * Save state for the redesigned builder's context bar.
 *
 * Client-side bookkeeping only — it never reads the database. Panes report `dirty`; the two Firestore
 * write funnels (CoursesDBService.saveCourse, LessonsDBService.saveLesson) report saving/saved/failed,
 * because panes reach them through a fire-and-forget dispatch and cannot see the outcome themselves.
 *
 * Replaces a header that read `course.updatedAt` and the global LoadingService: updatedAt is stamped on
 * the course document only, nothing re-reads the course after a write, and a failed write left the old
 * timestamp on screen — so a save that did not happen was reported as one that did.
 *
 * The shell is the only consumer. In the classic editor these signals are written and never read.
 */
@Injectable({ providedIn: 'root' })
export class BuilderSaveStateService {

  /** Keyed by source id so two panes cannot clear each other's state. Value is the tooltip label. */
  private readonly sources = signal<Record<string, { label: string; dirty: boolean }>>({});

  private readonly inFlight = signal(0);
  private readonly savedAt = signal<Date | null>(null);
  private readonly failureMessage = signal<string | null>(null);

  /**
   * Bumped by `reset()`. A write started before a pane or product switch still settles, but its
   * outcome is dropped rather than reported against whatever is on screen now.
   */
  private generation = 0;
  private fadeTimer: ReturnType<typeof setTimeout> | undefined;

  readonly dirty = computed(() => Object.values(this.sources()).some(s => s.dirty));
  readonly saving = computed(() => this.inFlight() > 0);
  readonly lastSavedAt = this.savedAt.asReadonly();
  readonly failure = this.failureMessage.asReadonly();

  readonly dirtyLabels = computed(() =>
    Object.values(this.sources()).filter(s => s.dirty).map(s => s.label));

  /**
   * Precedence: a failure beats dirty and saved, dirty beats saved so an old save cannot vouch for
   * what is being typed now, and idle claims nothing at all.
   */
  readonly status = computed<BuilderSaveStatus>(() => {
    if (this.saving()) return 'saving';
    if (this.failureMessage()) return 'failed';
    if (this.dirty()) return 'dirty';
    if (this.savedAt()) return 'saved';
    return 'idle';
  });

  /** Calling this registers `id` as a source that tracks its own dirtiness. */
  setDirty(id: string, dirty: boolean, label = id) {
    this.sources.update(current => {
      const existing = current[id];
      if (existing && existing.dirty === dirty && existing.label === label) return current;
      return { ...current, [id]: { label, dirty } };
    });
  }

  /** Must be called on pane destroy, or its dirty state outlives the pane. */
  release(id: string) {
    this.sources.update(current => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  /**
   * Report a write whose outcome the caller resolves itself. A fresh attempt with nothing else in
   * flight clears a previous failure — it is now the latest word, and will re-report if it fails too.
   */
  beginSave(): SaveTicket {
    const generation = this.generation;
    let settled = false;

    if (this.inFlight() === 0) this.failureMessage.set(null);
    this.inFlight.update(n => n + 1);

    return {
      ok: () => {
        if (settled) return;
        settled = true;
        this.settle(generation, null);
      },
      fail: (reason?: string) => {
        if (settled) return;
        settled = true;
        this.settle(generation, reason || 'Could not save.');
      },
    };
  }

  /**
   * Observe a write already under way. The promise is not awaited, returned or branched on — the
   * handlers create a separate derived promise, so the original rejects into its caller unchanged.
   */
  report(work: Promise<unknown> | null | undefined) {
    if (!work || typeof work.then !== 'function') return;

    const ticket = this.beginSave();
    work.then(
      () => ticket.ok(),
      (err: any) => ticket.fail(err?.message),
    );
  }

  /**
   * Called by the shell on pane or product change. Dirty sources are deliberately left alone: they
   * belong to the panes, which release them on destroy.
   */
  reset() {
    this.generation += 1;
    clearTimeout(this.fadeTimer);
    this.fadeTimer = undefined;
    this.savedAt.set(null);
    this.failureMessage.set(null);
  }

  private settle(generation: number, failure: string | null) {
    this.inFlight.update(n => Math.max(0, n - 1));

    if (generation !== this.generation) return;

    // of two writes fired together, the one that did not land is the one worth saying out loud
    if (failure) {
      this.failureMessage.set(failure);
      return;
    }

    this.savedAt.set(new Date());
    this.scheduleFade();
  }

  private scheduleFade() {
    clearTimeout(this.fadeTimer);
    this.fadeTimer = undefined;

    // something on screen tracks its own dirtiness, so the tick stays until that says otherwise
    if (Object.keys(this.sources()).length) return;

    this.fadeTimer = setTimeout(() => this.savedAt.set(null), UNTRACKED_SAVED_TTL_MS);
  }
}
